import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  OverlayTrigger,
  Row,
  Spinner,
  Table,
  Tooltip,
  Modal,
} from 'react-bootstrap'
import {
  CampaignChannel,
  ChannelVariant,
  ICampaign,
  ICampaignLog,
  ICampaignTarget,
  IMessageTemplate,
} from '../../../types/campaigns'
import { ChannelBadge } from './ChannelBadge'
import { ContactSearchSelect } from './ContactSearchSelect'
import clsx from 'clsx'
import { toast } from 'react-toastify'
import { VariantPreview } from '../../templates/components/VariantPreview'

type Props = {
  campaign?: ICampaign | null
  targets: ICampaignTarget[]
  logs: ICampaignLog[]
  loadingTargets?: boolean
  loadingLogs?: boolean
  onEditCampaign: () => void
  onAddTarget: () => void
  onLaunchCampaign: () => void
  onPauseCampaign: () => void
  launching?: boolean
  pausing?: boolean
  onSendTest: (
    recipient: string,
    channels: CampaignChannel[],
    advancedOptions?: {
      fromEmail?: string
      fromName?: string
      replyTo?: string
      includeLogo?: boolean
    }
  ) => Promise<void>
  onEditTemplate: (template: IMessageTemplate) => void
  onRefreshTargets: () => void
  onRefreshLogs: () => void
  onRemoveTarget: (targetId: string) => Promise<void>
  onDelete: () => Promise<void>
  autoRefresh?: boolean
  onToggleAutoRefresh?: (enabled: boolean) => void
}

const formatDate = (value?: string | null) => {
  if (!value) return 'Not set'
  try {
    return new Date(value).toLocaleString()
  } catch (error) {
    return value
  }
}

const channelOptions: CampaignChannel[] = ['SMS', 'EMAIL', 'WHATSAPP', 'TELEGRAM']

export const CampaignDetail = ({
  campaign,
  targets,
  logs,
  loadingTargets,
  loadingLogs,
  onEditCampaign,
  onAddTarget,
  onLaunchCampaign,
  onPauseCampaign,
  launching,
  pausing,
  onSendTest,
  onEditTemplate,
  onRefreshTargets,
  onRefreshLogs,
  onRemoveTarget,
  onDelete,
  autoRefresh = false,
  onToggleAutoRefresh,
}: Props) => {
  const navigate = useNavigate()
  const [testRecipient, setTestRecipient] = useState('')
  const [selectedChannels, setSelectedChannels] = useState<CampaignChannel[]>(['EMAIL'])
  const [sendingTest, setSendingTest] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showDeleteTargetModal, setShowDeleteTargetModal] = useState(false)
  const [selectedTarget, setSelectedTarget] = useState<ICampaignTarget | null>(null)
  const [showAdvancedTests, setShowAdvancedTests] = useState(false)
  const [previewVariant, setPreviewVariant] = useState<ChannelVariant | null>(null)

  // Advanced Test Options
  const [testFromEmail, setTestFromEmail] = useState('')
  const [testFromName, setTestFromName] = useState('')
  const [testReplyTo, setTestReplyTo] = useState('')
  const [testIncludeLogo, setTestIncludeLogo] = useState(false)

  const enabledChannels = useMemo(() => {
    const raw = campaign?.enabledChannels as unknown
    if (Array.isArray(raw)) return raw as CampaignChannel[]
    if (typeof raw === 'string') {
      return raw
        .split(',')
        .map((c) => c.trim())
        .filter(Boolean) as CampaignChannel[]
    }
    return []
  }, [campaign?.enabledChannels])

  const stats = useMemo(() => {
    if (!campaign) return []
    return [
      { label: 'Targets', value: campaign.totalTargets },
      { label: 'Sent', value: campaign.sentCount },
      { label: 'Delivered', value: campaign.deliveredCount },
      { label: 'Failed', value: campaign.failedCount },
    ]
  }, [campaign])

  const toggleChannel = (channel: CampaignChannel) => {
    setSelectedChannels((prev) => {
      if (prev.includes(channel)) {
        return prev.filter((item) => item !== channel)
      }
      return [...prev, channel]
    })
  }

  const handleSendTest = async () => {
    if (!testRecipient || selectedChannels.length === 0) return
    setSendingTest(true)
    try {
      await onSendTest(testRecipient, selectedChannels, {
        fromEmail: testFromEmail || undefined,
        fromName: testFromName || undefined,
        replyTo: testReplyTo || undefined,
        includeLogo: testIncludeLogo,
      })
      setTestRecipient('')
    } finally {
      setSendingTest(false)
    }
  }

  const handleDeleteTargetClick = (target: ICampaignTarget) => {
    setSelectedTarget(target)
    setShowDeleteTargetModal(true)
  }

  const confirmDeleteTarget = async () => {
    if (!selectedTarget) return

    try {
      await onRemoveTarget(selectedTarget.id)
      toast.success('Target removed successfully')
      setShowDeleteTargetModal(false)
      setSelectedTarget(null)
    } catch (error) {
      console.error(error)
      toast.error('Error removing target')
    }
  }

  if (!campaign) {
    return (
      <div className='campaigns-detail card h-100'>
        <div className='card-body d-flex align-items-center justify-content-center text-muted'>
          Select a campaign to see its details
        </div>
      </div>
    )
  }

  return (
    <>
      <div className='campaigns-detail'>
        <div className='campaigns-detail__header card mb-5'>
          <div className='card-body'>
            <div className='d-flex justify-content-between align-items-start flex-wrap gap-4'>
              <div>
                <h3 className='mb-1'>{campaign.name}</h3>
                <div className='text-muted'>{campaign.description || 'No description added'}</div>
                <div className='mt-3 d-flex flex-wrap gap-2'>
                  {enabledChannels.map((channel) => (
                    <ChannelBadge
                      key={`${campaign.id}-${channel}`}
                      channel={channel as CampaignChannel}
                    />
                  ))}
                </div>
              </div>
              <div className='d-flex gap-2'>
                <div className='d-flex align-items-center me-2'>
                  {autoRefresh && (
                    <Spinner animation='grow' variant='primary' size='sm' className='me-2' />
                  )}
                  <Form.Check
                    type="switch"
                    id="auto-refresh-switch"
                    label="Auto-refresh"
                    checked={autoRefresh}
                    onChange={(e) => onToggleAutoRefresh && onToggleAutoRefresh(e.target.checked)}
                    className="mb-0"
                  />
                </div>
                <Button
                  variant='light'
                  onClick={() => {
                    onRefreshTargets()
                    onRefreshLogs()
                  }}
                  title='Refresh data'
                >
                  <i className='bi bi-arrow-clockwise'></i>
                </Button>
                {campaign.status === 'DRAFT' && (
                  <Button
                    variant='light-danger'
                    onClick={() => setShowDeleteModal(true)}
                    title='Delete campaign'
                  >
                    <i className='bi bi-trash'></i>
                  </Button>
                )}
                <Button variant='light' onClick={onEditCampaign}>
                  Edit campaign
                </Button>
                <Button variant='secondary' onClick={onAddTarget}>
                  Add target
                </Button>
                {campaign.status === 'ACTIVE' ? (
                  <Button variant='warning' onClick={onPauseCampaign} disabled={pausing}>
                    {pausing && <Spinner animation='border' size='sm' className='me-2' />}
                    Pause
                  </Button>
                ) : (
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      campaign.totalTargets === 0 ? (
                        <Tooltip id="tooltip-launch">Add at least one target before launching.</Tooltip>
                      ) : (
                        <></>
                      )
                    }
                  >
                    <span className="d-inline-block">
                      <Button
                        variant='success'
                        onClick={onLaunchCampaign}
                        disabled={launching || campaign.totalTargets === 0}
                        style={campaign.totalTargets === 0 ? { pointerEvents: 'none' } : {}}
                      >
                        {launching && <Spinner animation='border' size='sm' className='me-2' />}
                        Launch
                      </Button>
                    </span>
                  </OverlayTrigger>
                )}
              </div>
            </div>
            <div className='mt-4'>
              <div className='d-flex flex-wrap gap-4 text-gray-700'>
                <span>
                  <strong>Status:</strong> {campaign.status}
                </span>
                <span>
                  <strong>Schedule:</strong> {campaign.scheduleType}
                </span>
                <span>
                  <strong>Next run:</strong> {formatDate(campaign.scheduledTime)}
                </span>
                <span>
                  <strong>Last run:</strong> {formatDate(campaign.lastRunAt)}
                </span>
              </div>
            </div>
          </div>
        </div>

        <Row className='g-5 mb-5'>
          <Col xl={8}>
            <Card>
              <Card.Header>
                <Card.Title>Performance</Card.Title>
                <div className='text-muted fs-7'>Delivery metrics refresh after each execution</div>
              </Card.Header>
              <Card.Body>
                <Row className='g-4'>
                  {stats.map((stat) => (
                    <Col md={3} key={stat.label}>
                      <div className='campaigns-stat'>
                        <div className='campaigns-stat__label'>{stat.label}</div>
                        <div className='campaigns-stat__value'>{stat.value ?? 0}</div>
                      </div>
                    </Col>
                  ))}
                </Row>
                {/* {campaign.statistics && (
                <div className='mt-4'>
                  <div className='text-muted mb-2'>Raw statistics</div>
                  <pre className='campaigns-json'>{JSON.stringify(campaign.statistics, null, 2)}</pre>
                </div>
              )} */}
              </Card.Body>
            </Card>
            <Card className='mt-5'>
              <Card.Header>
                <Card.Title>Targets</Card.Title>
                <div className='card-toolbar d-flex gap-2'>
                  <Button size='sm' variant='light' onClick={() => onRefreshTargets()}>
                    Refresh
                  </Button>
                  <Button size='sm' onClick={() => onAddTarget()}>
                    Add target
                  </Button>
                </div>
              </Card.Header>
              <Card.Body className='p-0'>
                {loadingTargets ? (
                  <div className='campaigns-placeholder'>
                    <Spinner animation='border' size='sm' className='me-2' />
                    Loading targets
                  </div>
                ) : targets.length === 0 ? (
                  <div className='campaigns-placeholder'>No targets added yet</div>
                ) : (
                  <div className='table-wrapper' style={{ opacity: loadingTargets ? 0.5 : 1, transition: 'opacity 0.2s' }}>
                    <div className='table-responsive table table-striped table-hover '>
                      <Table
                        hover
                        className='table-scrollable table-scroll  table table-striped table-hover '
                        style={{ whiteSpace: 'nowrap' }}
                      >
                        <thead>
                          <tr>
                            <th>Contact</th>
                            <th>Email</th>
                            <th>Phone</th>
                            <th>Status</th>
                            <th>Last channel</th>
                            <th>Errors</th>
                            <th className='text-end'>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {targets.map((target) => (
                            <tr key={target.id}>
                              <td>
                                {`${target.contact.firstName ?? ''} ${target.contact.lastName ?? ''
                                  }`.trim() || target.contact.id}
                              </td>
                              <td>{target.contact.email || '—'}</td>
                              <td>{target.contact.phone || '—'}</td>
                              <td>
                                <span
                                  className={clsx(
                                    'badge',
                                    `badge-light-${target.state === 'FAILED' ? 'danger' : 'primary'}`
                                  )}
                                >
                                  {target.state}
                                </span>
                              </td>
                              <td>
                                {target.lastChannel ? (
                                  <ChannelBadge channel={target.lastChannel} />
                                ) : (
                                  '—'
                                )}
                              </td>
                              <td>{target.errorCount}</td>
                              <td className='text-end'>
                                <Button
                                  size='sm'
                                  variant='light-danger'
                                  className='btn-icon'
                                  onClick={() => handleDeleteTargetClick(target)}
                                >
                                  <i className='bi bi-trash fs-5'></i>
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
            <Card className='mt-5'>
              <Card.Header>
                <Card.Title>Activity log</Card.Title>
                <Button size='sm' variant='light' onClick={onRefreshLogs}>
                  Refresh
                </Button>
              </Card.Header>
              <Card.Body className='p-0'>
                {loadingLogs ? (
                  <div className='campaigns-placeholder'>
                    <Spinner animation='border' size='sm' className='me-2' />
                    Loading logs
                  </div>
                ) : logs.length === 0 ? (
                  <div className='campaigns-placeholder'>No logs found for this campaign</div>
                ) : (
                  <div className='table-wrapper' style={{ opacity: loadingLogs ? 0.5 : 1, transition: 'opacity 0.2s' }}>
                    <div className='table-responsive table table-striped table-hover '>
                      <Table
                        hover
                        className='table-scrollable table-scroll  table table-striped table-hover '
                        style={{ whiteSpace: 'nowrap' }}
                      >
                        <thead>
                          <tr>
                            <th>Timestamp</th>
                            <th>Channel</th>
                            <th>Status</th>
                            <th>Recipient</th>
                            <th>Provider ID</th>
                            <th>Error</th>
                          </tr>
                        </thead>
                        <tbody>
                          {logs.map((log) => (
                            <tr key={log.id}>
                              <td>{formatDate(log.createdAt)}</td>
                              <td>
                                <ChannelBadge channel={log.channel} />
                              </td>
                              <td>
                                <span
                                  className={clsx(
                                    'badge',
                                    log.status === 'FAILED'
                                      ? 'badge-light-danger'
                                      : 'badge-light-success'
                                  )}
                                >
                                  {log.status}
                                </span>
                              </td>
                              <td>
                                {log.contact
                                  ? `${log.contact.firstName ?? ''} ${log.contact.lastName ?? ''
                                    }`.trim() ||
                                  log.contact.email ||
                                  log.contact.id
                                  : '—'}
                              </td>
                              <td>{log.providerMessageId || '—'}</td>
                              <td>
                                {log.errorMessage ? (
                                  <OverlayTrigger
                                    overlay={
                                      <Tooltip id={`log-error-${log.id}`}>{log.errorMessage}</Tooltip>
                                    }
                                  >
                                    <span className='text-danger'>View</span>
                                  </OverlayTrigger>
                                ) : (
                                  '—'
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
          <Col xl={4}>
            {campaign.template ? (
              <Card className='mb-5'>
                <Card.Header>
                  <Card.Title>Template: {campaign.template.name}</Card.Title>
                  <Button
                    size='sm'
                    variant='light'
                    onClick={() => {
                      // Navigate to full editor instead of simple modal
                      navigate(`/templates/edit/${campaign.template!.id}`)
                    }}
                  >
                    Edit template
                  </Button>
                </Card.Header>
                <Card.Body>
                  <div className='d-flex flex-wrap gap-2 mb-3'>
                    {campaign.template.variants && campaign.template.variants.length > 0 ? (
                      campaign.template.variants.map((variant) => (
                        <div key={variant.id} className="w-100 border rounded mb-3 overflow-hidden">
                          <div className="d-flex align-items-center justify-content-between p-3 bg-light">
                            <div className="d-flex align-items-center gap-2">
                              <ChannelBadge channel={variant.channel} />
                              {variant.subject && <span className="fw-bold">{variant.subject}</span>}
                            </div>
                            <div className="d-flex gap-2">
                              <Button
                                size="sm"
                                variant="white"
                                className="btn-icon"
                                onClick={() => setPreviewVariant(variant)}
                              >
                                <i className="bi bi-eye"></i>
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="w-100">
                        <div className="mb-2">
                          <ChannelBadge channel={campaign.template.channel} />
                        </div>
                        {campaign.template.subject && (
                          <div className='mb-2'><strong>Subject:</strong> {campaign.template.subject}</div>
                        )}
                        <div className='text-gray-600 small'>
                          {campaign.template.description || 'No description provided'}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className='text-gray-600 small mt-2'>
                    {campaign.template.description}
                  </div>
                </Card.Body>
              </Card>
            ) : (
              <Alert variant='light' className='mb-5'>
                This campaign has no template. Assign one before launching.
              </Alert>
            )}
            <Card className='mb-5'>
              <Card.Header>
                <Card.Title>Send test message</Card.Title>
              </Card.Header>
              <Card.Body>
                <Form.Group className='mb-3'>
                  <Form.Label>Recipient</Form.Label>
                  <Form.Control
                    type="text"
                    value={testRecipient}
                    onChange={(e) => setTestRecipient(e.target.value)}
                    placeholder='Enter email or phone...'
                  />
                </Form.Group>
                <div className='mb-3'>
                  <div className='fw-semibold mb-2'>Channels</div>
                  <div className='d-flex flex-wrap gap-3'>
                    {channelOptions.map((channel) => (
                      <Form.Check
                        type='checkbox'
                        key={channel}
                        label={channel}
                        checked={selectedChannels.includes(channel)}
                        onChange={() => toggleChannel(channel)}
                      />
                    ))}
                  </div>
                </div>

                <div className="mb-4">
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 text-primary text-decoration-none"
                    onClick={() => setShowAdvancedTests(!showAdvancedTests)}
                  >
                    <i className={`bi bi-chevron-${showAdvancedTests ? 'up' : 'down'} me-1`}></i>
                    Advanced Options
                  </Button>

                  {showAdvancedTests && (
                    <div className="mt-3 p-3 bg-light rounded border">
                      <Form.Group className="mb-3">
                        <Form.Label className="small fw-bold">From Name</Form.Label>
                        <Form.Control
                          size="sm"
                          type="text"
                          placeholder="e.g. Marketing Team"
                          value={testFromName}
                          onChange={(e) => setTestFromName(e.target.value)}
                        />
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label className="small fw-bold">From Email</Form.Label>
                        <Form.Control
                          size="sm"
                          type="email"
                          placeholder="e.g. news@company.com"
                          value={testFromEmail}
                          onChange={(e) => setTestFromEmail(e.target.value)}
                        />
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label className="small fw-bold">Reply-To</Form.Label>
                        <Form.Control
                          size="sm"
                          type="email"
                          placeholder="e.g. support@company.com"
                          value={testReplyTo}
                          onChange={(e) => setTestReplyTo(e.target.value)}
                        />
                      </Form.Group>
                      <Form.Check
                        type="checkbox"
                        label="Include Company Logo"
                        className="small fw-bold"
                        checked={testIncludeLogo}
                        onChange={(e) => setTestIncludeLogo(e.target.checked)}
                      />
                    </div>
                  )}
                </div>

                <div className='d-flex gap-2'>
                  <div className='d-flex align-items-center me-2'>
                    {autoRefresh && (
                      <Spinner animation='grow' variant='primary' size='sm' className='me-2' />
                    )}
                    <Form.Check
                      type="switch"
                      id="auto-refresh-switch"
                      label="Auto-refresh"
                      checked={autoRefresh}
                      onChange={(e) => onToggleAutoRefresh && onToggleAutoRefresh(e.target.checked)}
                      className="mb-0"
                    />
                  </div>
                  <Button
                    variant='primary'
                    disabled={!testRecipient || selectedChannels.length === 0 || sendingTest}
                    onClick={handleSendTest}
                  >
                    {sendingTest && <Spinner animation='border' size='sm' className='me-2' />}
                    Send test
                  </Button>
                </div>
              </Card.Body>
            </Card>
            {/* <Card>
            <Card.Header>
              <Card.Title>Metadata</Card.Title>
            </Card.Header>
            <Card.Body>
              <div className='text-muted mb-2'>Target segment</div>
              <pre className='campaigns-json mb-4'>
                {JSON.stringify(campaign.targetSegment ?? {}, null, 2)}
              </pre>
              <div className='text-muted mb-2'>Campaign metadata</div>
              <pre className='campaigns-json'>
                {JSON.stringify(campaign.metadata ?? {}, null, 2)}
              </pre>
            </Card.Body>
          </Card> */}
          </Col>
        </Row>
      </div >

      {/* Preview Modal */}
      <Modal
        show={!!previewVariant}
        onHide={() => setPreviewVariant(null)}
        size={previewVariant?.channel === 'EMAIL' ? 'xl' : undefined}
        centered
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Preview - {previewVariant?.channel}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-light p-0">
          <div className="d-flex justify-content-center py-4">
            {previewVariant && (
              <VariantPreview
                variant={previewVariant}
                variables={{
                  firstName: 'John',
                  lastName: 'Doe',
                  email: 'john@example.com',
                  phone: '+1234567890'
                }}
              />
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setPreviewVariant(null)}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      < Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered >
        <Modal.Header closeButton className='border-0 pb-0'>
          <Modal.Title className='text-danger'>Delete Campaign</Modal.Title>
        </Modal.Header>
        <Modal.Body className='pt-4 pb-4'>
          <div className='text-center mb-4'>
            <div className='btn btn-icon btn-light-danger btn-circle btn-lg mb-3'>
              <i className='bi bi-trash fs-1'></i>
            </div>
            <h5>Are you sure?</h5>
            <div className='text-muted'>
              You are about to delete the campaign <span className='fw-bold'>"{campaign?.name}"</span>.
              This action cannot be undone.
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className='border-0 justify-content-center pb-4'>
          <Button variant='light' onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button
            variant='danger'
            onClick={async () => {
              await onDelete()
              setShowDeleteModal(false)
            }}
          >
            Delete Campaign
          </Button>
        </Modal.Footer>
      </Modal >

      {/* Delete Target Confirmation Modal */}
      < Modal show={showDeleteTargetModal} onHide={() => setShowDeleteTargetModal(false)} centered >
        <Modal.Header closeButton className='border-0 pb-0'>
          <Modal.Title>Remove Target</Modal.Title>
        </Modal.Header>
        <Modal.Body className='pt-3 pb-4'>
          <div className='alert alert-warning d-flex align-items-start' role='alert'>
            <i className='bi bi-exclamation-circle me-2 fs-4'></i>
            <div>
              Are you sure you want to remove the target{' '}
              <strong>
                {selectedTarget?.contact.firstName || selectedTarget?.contact.lastName
                  ? `${selectedTarget?.contact.firstName ?? ''} ${selectedTarget?.contact.lastName ?? ''}`.trim()
                  : selectedTarget?.contact.email || selectedTarget?.contact.id}
              </strong>
              ?
              <div className='mt-2 small'>
                This action cannot be undone.
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className='border-0 justify-content-end'>
          <Button variant='light' onClick={() => setShowDeleteTargetModal(false)}>
            Cancel
          </Button>
          <Button variant='danger' onClick={confirmDeleteTarget}>
            Remove
          </Button>
        </Modal.Footer>
      </Modal >
    </>
  )
}
